import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Check, Moon } from "lucide-react";
import { DELIVERY_OPTIONS } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.16, 1, 0.3, 1];

export default function PendulumIntake({ sessionId, tierName, quantity = 1 }) {
  const count = Math.max(1, quantity);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [delivery, setDelivery] = useState(DELIVERY_OPTIONS[0]);
  const [phone, setPhone] = useState("");
  const [questions, setQuestions] = useState(Array.from({ length: count }, () => ""));
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const setQ = (i, v) => setQuestions((qs) => qs.map((q, idx) => (idx === i ? v : q)));

  const submit = async (e) => {
    e.preventDefault();
    const filled = questions.map((q) => q.trim()).filter(Boolean);
    if (!name.trim() || !email.includes("@")) {
      toast.error("Please add your name and a valid email.");
      return;
    }
    if (filled.length === 0) {
      toast.error("Please write at least one yes/no question.");
      return;
    }
    if (delivery === "Phone Call" && !phone.trim()) {
      toast.error("Please add a phone number for your call.");
      return;
    }
    setSubmitting(true);
    const finalNote = delivery === "Phone Call" && phone.trim()
      ? `Phone: ${phone.trim()}${note ? ` — ${note}` : ""}`
      : note;
    try {
      await axios.post(`${API}/appointments/pendulum`, {
        session_id: sessionId,
        name,
        email,
        delivery_method: delivery,
        questions: filled,
        note: finalNote,
        origin_url: window.location.origin,
      });
      setDone(true);
      toast.success("Your pendulum session is confirmed ✦");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something wavered. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full rounded-sm bg-[#140B09] border border-[#C9A24B]/25 px-4 py-3 text-[#EFE3CC] placeholder:text-[#7a6a55] outline-none focus:border-[#C77E8A] focus:ring-1 focus:ring-[#C77E8A]/40 transition-all";

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease }}
        className="gold-frame rounded-sm bg-[#1F130E] p-10 max-w-md w-full flex flex-col items-center text-center"
        data-testid="pendulum-success"
      >
        <div className="w-16 h-16 rounded-full border border-[#C9A24B]/40 flex items-center justify-center glow-gold mb-6">
          <Check className="w-7 h-7 text-[#C9A24B]" strokeWidth={1.5} />
        </div>
        <h1 className="font-display text-3xl text-[#EFE3CC]">Your questions are received ✦</h1>
        <p className="mt-3 text-[#B7A488] font-light">
          I&apos;ll consult the pendulum and send your answers by <strong className="text-[#E9D3A0]">{delivery}</strong> within 24 hours.
        </p>
        <a href="/" className="mt-8 rounded-full border border-[#C9A24B]/45 px-8 py-3 text-xs tracking-[0.2em] uppercase text-[#EFE3CC] hover:border-[#C9A24B] hover:bg-[#C9A24B]/10 transition-all">
          Back to Home
        </a>
      </motion.div>
    );
  }

  return (
    <motion.form
      onSubmit={submit}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease }}
      className="gold-frame rounded-sm bg-[#1F130E] p-8 sm:p-10 max-w-xl w-full text-left"
      data-testid="pendulum-intake"
    >
      <div className="flex flex-col items-center text-center mb-8">
        <Moon className="w-7 h-7 text-[#C9A24B] glow-gold mb-4" strokeWidth={1.2} />
        <span className="text-[11px] tracking-[0.4em] uppercase text-[#C9A24B] mb-2">✦ Payment Confirmed ✦</span>
        <h1 className="font-display text-3xl sm:text-4xl text-[#EFE3CC] leading-tight">{tierName}</h1>
        <p className="mt-3 text-sm text-[#B7A488] font-light">
          Ask your yes/no question{count > 1 ? "s" : ""} below and choose how you&apos;d like your answer{count > 1 ? "s" : ""} delivered.
        </p>
      </div>

      {/* Delivery method */}
      <label className="block text-[11px] tracking-[0.18em] uppercase text-[#B7A488] mb-2">How would you like your answers?</label>
      <div className="grid grid-cols-2 gap-3 mb-4" data-testid="pendulum-delivery-toggle">
        {DELIVERY_OPTIONS.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => setDelivery(opt)}
            data-testid={`delivery-${opt.replace(/\s/g, "-").toLowerCase()}`}
            className={`flex items-center justify-center gap-2 rounded-sm px-3 py-3 text-[11px] tracking-[0.1em] uppercase transition-all ${
              delivery === opt
                ? "bg-[#C9A24B]/15 border border-[#C9A24B] text-[#EFE3CC]"
                : "border border-[#C9A24B]/25 text-[#B7A488] hover:border-[#C9A24B]/50"
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
      {delivery === "Phone Call" ? (
        <input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="pendulum-phone" placeholder="Best phone number for your call" className={`${inputCls} mb-6`} />
      ) : (
        <div className="mb-6" />
      )}

      {/* Questions */}
      <label className="block text-[11px] tracking-[0.18em] uppercase text-[#B7A488] mb-2">
        Your yes/no question{count > 1 ? "s" : ""}
      </label>
      <div className="space-y-3 mb-6">
        {questions.map((q, i) => (
          <input
            key={i}
            value={q}
            onChange={(e) => setQ(i, e.target.value)}
            data-testid={`pendulum-question-${i}`}
            placeholder={count > 1 ? `Question ${i + 1}` : "e.g., Is now the right time to make my move?"}
            className={inputCls}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="pendulum-name" placeholder="Your name" className={inputCls} />
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="pendulum-email" placeholder="Your email" className={inputCls} />
      </div>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        data-testid="pendulum-note"
        placeholder="Anything else I should know? (optional)"
        rows={2}
        className={`${inputCls} mb-6 resize-none`}
      />

      <button
        type="submit"
        disabled={submitting}
        data-testid="pendulum-submit"
        className="w-full rounded-full bg-gradient-to-r from-[#9C2A52] to-[#C77E8A] py-3.5 text-xs tracking-[0.2em] uppercase text-[#EFE3CC] transition-transform hover:scale-[1.02] disabled:opacity-60 flex items-center justify-center gap-2"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Send My Question{count > 1 ? "s" : ""}
      </button>
    </motion.form>
  );
}
